import React, { useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { MenuItemType } from '@next-nx/utils/types'
import { getIcon } from '@next-nx/utils/icons'
import { ArrowRight } from '@next-nx/shared-ui/icons'
import { Button, IconBg, NumeroVerde } from '@next-nx/shared-ui/atoms'
import { CircleBox, CircleBoxSmall, OffertaCard } from '@next-nx/shared-ui/molecules'
import { useTealium, useTrans } from '@next-nx/hooks'
import styles from './NavMobile.module.sass'
import Link from 'next/link'
import { getTimestamp, getAnchorRel } from '@next-nx/utils'

interface Props {
  item: MenuItemType
  commonItem: MenuItemType
  open: boolean
  setOpen: (v: boolean) => void
  className?: string
}

const NavMobile = (props: Props) => {
  const {
    item = undefined,
    commonItem = undefined,
    open = false,
    setOpen = () => undefined,
    className = '',
  } = props

  const [activeTab, setActiveTab] = useState(
    item?.children?.edges.filter((x) => x.node.style === 'tabs')?.[0]?.node
  )
  const t = useTrans()
  const { linkDataLayer } = useTealium()

  const handleTracking = (
    action_type = '',
    cta = '-',
    info: string | undefined = '',
    interazione?: number
  ) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}

    switch (interazione) {
      case 1:
        dlObj = {
          virtual: 'action',
          action_name: 'menu',
          action_tech: 'click',
          action_type: action_type.replace(/\.|\/|\s+/g, '_').toLocaleLowerCase(),
          action_timestamp,
          action_effect: 'navigazione_menu',
          cta: cta.replace(/\.|\/|\s+/g, '_').toLowerCase(),
          info: info?.replace(/\.|\/|\s+/g, '_').toLowerCase(),
        }
        break
      case 2:
        dlObj = {
          virtual: 'action',
          action_name: 'navigazione',
          action_tech: 'click',
          action_type: action_type?.toLowerCase().includes('noleggio a lungo termine')
            ? 'scopri_il_nlt'
            : action_type.replace(/\.|\/|\s+/g, '_').toLocaleLowerCase(),
          action_timestamp,
          action_effect: 'navigazione_header',
          cta: cta.replace(/\.|\/|\s+/g, '_').toLowerCase(),
        }
        break
      case 3:
        dlObj = {
          virtual: 'action',
          action_name: 'menu',
          action_tech: 'click',
          action_type: action_type.replace(/\.|\/|\s+/g, '_').toLocaleLowerCase(),
          action_timestamp,
          action_effect: 'navigazione_menu',
          cta: cta.replace(/\.|\/|\s+/g, '_').toLowerCase(),
          info: info.replace(/\.|\/|\s+/g, '_').toLowerCase(),
        }
        break
      default:
        break
    }
    linkDataLayer(dlObj)
  }

  return item ? (
    <>
      <div
        className={`${styles.overlay} ${open ? styles['overlay--open'] : ''}`}
        onClick={() => setOpen(false)}
      />
      <div className={`${styles.root} ${open ? styles['root--open'] : ''} ${className}`}>
        {item.children?.edges
          .filter((x) => x.node.pk === activeTab?.pk)
          .map((x) => (
            <React.Fragment key={x.node.pk}>
              {x.node.style === 'tabs' && (
                <div className={styles.tabs}>
                  <div className={styles.tabTriggers}>
                    {item.children?.edges
                      .filter((y) => y.node.style === 'tabs')
                      .map((y) => (
                        <button
                          key={y.node.pk}
                          role="button"
                          className={`${styles.tabTrigger} ${
                            activeTab?.pk === y.node.pk ? styles['tabTrigger--active'] : ''
                          } ${y.node.name?.toHotjarFormat('dropdown_{0}', true)}`}
                          onClick={() => setActiveTab(y.node)}
                        >
                          {y.node.name}
                        </button>
                      ))}
                  </div>
                  {activeTab?.children && activeTab?.children?.edges.length > 0 && (
                    <div className={styles.tab}>
                      {activeTab.children.edges.map((y) => (
                        <React.Fragment key={y.node.pk}>
                          {y.node.key === 'soluzioni' && !!y.node.children?.edges.length && (
                            <div className={styles.soluzioni}>
                              <Swiper
                                slidesPerView={2.5}
                                centerInsufficientSlides
                                breakpoints={{ 374: { slidesPerView: 3.5 } }}
                              >
                                {y.node.children?.edges.map((z, i) => (
                                  <SwiperSlide key={z.node.pk}>
                                    <Link href={z.node.url ? z.node.url : '#'}>
                                      <a
                                        className={styles.soluzioniItem}
                                        rel={getAnchorRel(z.node.url ? z.node.url : '')}
                                        onClick={() =>
                                          handleTracking(
                                            activeTab?.name,
                                            y.node.name,
                                            z.node.name,
                                            1
                                          )
                                        }
                                      >
                                        <IconBg
                                          icon={getIcon(z.node.style)}
                                          animationVariant={i % 2 === 0 ? 'se' : 'nw'}
                                          className={styles.soluzioniItemIcon}
                                        />
                                        <span className={styles.soluzioniItemLabel}>
                                          {z.node.name}
                                        </span>
                                      </a>
                                    </Link>
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                            </div>
                          )}
                          {y.node.key === 'box_small' && (
                            <div className={styles.smallBox}>
                              <CircleBoxSmall
                                item={{ ...y.node, children: undefined }}
                                handleTracking={() => handleTracking(y.node.name, '-', '', 2)}
                              />
                            </div>
                          )}
                          {y.node.key === 'prodotti' &&
                            !!y.node.children?.edges.length &&
                            !!y.node.children.edges.some((c) => !!c.node.offerta) && (
                              <div className={styles.prodotti}>
                                <p className={styles.prodottiTitle}>{y.node.nameMobile}</p>
                                <p className={styles.prodottiSubtitle}>{activeTab.name}</p>
                                <div className={styles.prodottiList}>
                                  <Swiper slidesPerView={1.01} centerInsufficientSlides>
                                    {y.node.children?.edges.map(
                                      (z) =>
                                        z.node.offerta && (
                                          <SwiperSlide key={z.node.pk}>
                                            <OffertaCard
                                              offerta={z.node.offerta}
                                              codiceCategoriaPrimaria={activeTab.key}
                                              variant="menu"
                                              sezione={activeTab.name}
                                              azione="menu"
                                            />
                                          </SwiperSlide>
                                        )
                                    )}
                                  </Swiper>
                                </div>
                                {y.node.ctaLabel && y.node.url && (
                                  <div className={styles.prodottiCtaBox}>
                                    <Button
                                      label={y.node.ctaLabel}
                                      href={y.node.url}
                                      variant="ghost"
                                      icon={<ArrowRight />}
                                      iconPos="right"
                                      handleTracking={() =>
                                        handleTracking(
                                          activeTab.name,
                                          y.node.name,
                                          y.node.ctaLabel,
                                          3
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {commonItem &&
                commonItem.children &&
                commonItem.children.edges.length > 0 &&
                commonItem.children.edges.map((y) => (
                  <React.Fragment key={y.node.pk}>
                    {y.node.key === 'servizi' && (
                      <div className={styles.link}>
                        <p className={styles.linkTitle}>{y.node.name}</p>
                        <div className={styles.linkList}>
                          {y.node.children?.edges.map((z) => (
                            <Link href={z.node.url || '#'} key={z.node.pk}>
                              <a
                                className={styles.linkItem}
                                rel={getAnchorRel(z.node.url || '')}
                                onClick={() => handleTracking(z.node.name, '-', '', 2)}
                              >
                                <span className={styles.linkItemLabel}>{z.node.name}</span>
                                <span className={styles.linkItemIcon}>
                                  <ArrowRight />
                                </span>
                              </a>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                    {y.node.key === 'box' && (
                      <div className={styles.box}>
                        <CircleBox
                          item={{ ...y.node, children: undefined }}
                          animationVariant="vertical"
                          size="sm"
                          handleTracking={() =>
                            handleTracking(y.node.name, y.node.ctaLabel || '-', '-', 2)
                          }
                        />
                      </div>
                    )}
                    {y.node.key === 'numero_verde' && (
                      <div className={styles.numeroVerde}>
                        <NumeroVerde
                          phoneLabel={t('Numero Verde')}
                          description={t(
                            'Hai bisogno del soccorso stradale?<br />Chiama il numero verde gratuito'
                          )}
                        />
                      </div>
                    )}
                    {y.node.key === 'help' && !!y.node.children?.edges.length && (
                      <div className={styles.help}>
                        <p className={styles.helpTitle}>{y.node.name}</p>
                        <div className={styles.helpList}>
                          {y.node.children.edges.map((z) => (
                            <div className={styles.helpItem} key={z.node.pk}>
                              <Button
                                label={z.node.nameMobile}
                                href={z.node.url}
                                variant="secondary"
                                color="white"
                                onClick={() => handleTracking(z.node.name, '-', '', 2)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
      </div>
    </>
  ) : (
    <></>
  )
}

export default NavMobile
